import { convertFileResponse } from "@relode/core/backend/responses";
import { JobType, Tag } from "@relode/core/domain";

import { converters } from ".";
import {
    JobManagerContact,
    CandidateAdditionalInfo,
    CandidateAvailability,
    CandidateDetails,
    CandidateHighlights,
    CandidateJobDetails,
} from "../../types";
import { TagResponse } from "../controllers/tags/tagResponse";
import {
    JobManagerContactPublicResponse,
    CandidateAdditionalInfoPublicResponse,
    CandidateAvailabilityPublicResponse,
    JobDetailsPublicResponse,
    LeadHighlightsPublicResponse,
    LeadJobDetailPublicResponse,
} from "../controllers/candidatesPublic";
import { convertLeadJobQualificationsResponse } from "./convertLeadJobQualificationsResponse";

export const convertLeadJobDetailPublicResponse = (
    response: LeadJobDetailPublicResponse
): CandidateDetails => {
    const convertCandidateOverview = (
        response: LeadHighlightsPublicResponse
    ): CandidateHighlights => {
        if (!response) return null;

        const highlights: CandidateHighlights = {
            leadId: response.leadId,
            fullName: response.displayName,
            firstName: response.firstName,
            candidateStatusId: response.candidateStatusId,
            careerHighlightNote: response.careerHighlightNote,
            canViewDesiredSalary: false,
            canContactLead: true,
            email: response.email,
            phoneNumber: response.phoneNumber,

            // not included in public response
            attachments: undefined,
            desiredSalaryDescription: undefined,
            desiredSalaryIsRate: undefined,
            links: undefined,
            startDate: undefined,
            primaryLocationName: response.primaryLocationName,
        };

        return highlights;
    };

    const convertAvailabilityResponse = (
        response: CandidateAvailabilityPublicResponse
    ): CandidateAvailability => {
        const availability: CandidateAvailability = {
            interviewAvailabilityNote: response.interviewAvailabilityNote,
            noticeAvailability: converters.convertTagTypeResponse(
                response.noticeAvailability
            ),
            contactPreferences: converters.convertTagTypeResponseList(
                response.contactPreferences
            ),
            timeZoneId: response.timeZoneId,
        };

        return availability;
    };

    const convertAdditionalInfo = (
        response: CandidateAdditionalInfoPublicResponse
    ): CandidateAdditionalInfo => {
        if (!response) return null;
        const additionalInfo: CandidateAdditionalInfo = {
            interviewingElsewhereNote: response.interviewingElsewhereNote,
            isInterviewingElsewhere: response.isInterviewingElsewhere,
            isWillingToRelocate: convertLeadTagResponse(
                response.isWillingToRelocate
            ),
            isUserPrimaryLocationWithinJobLocation:
                response.isUserPrimaryLocationWithinJobLocation,
        };

        return additionalInfo;
    };

    const convertJobDetailsResponse = (
        response: JobDetailsPublicResponse
    ): CandidateJobDetails => {
        if (!response) return null;

        const job = new CandidateJobDetails();
        job.jobId = response.jobId;
        job.jobName = response.jobName;
        job.jobType = JobType.fromId(response.jobTypeId);
        job.location = converters.convertLocationResponse(response.location);

        return job;
    };

    const convertLeadTagResponse = (response: TagResponse): Tag => {
        if (!response) return null;

        const ret = converters.convertTagResponse(response);

        return ret;
    };

    const convertJobManagerContactResponse = (
        response: JobManagerContactPublicResponse
    ): JobManagerContact => {
        if (!response) return null;

        const jmContact = new JobManagerContact();
        jmContact.fullName = response.fullName;
        jmContact.email = response.email;
        jmContact.phoneNumber = response.phoneNumber;
        jmContact.profileImageUrl = response.profileImageUrl;
        jmContact.initials = response.initials;

        return jmContact;
    };

    const candidateDetails = new CandidateDetails();
    candidateDetails.highlights = convertCandidateOverview(response.highlights);
    candidateDetails.additionalInfo = convertAdditionalInfo(
        response.additionalInfo
    );
    candidateDetails.formattedResume = convertFileResponse(
        response.formattedResume
    );
    candidateDetails.availability = convertAvailabilityResponse(
        response.availability
    );
    candidateDetails.job = convertJobDetailsResponse(response.job);
    candidateDetails.jobManager = convertJobManagerContactResponse(
        response.jobManager
    );
    candidateDetails.redirectToPrivate = response.redirectToPrivate;
    candidateDetails.showGiveFeedback = response.showGiveFeedback;
    candidateDetails.qualifications = convertLeadJobQualificationsResponse(
        response.leadJobQualifications
    );

    return candidateDetails;
};
